import { Header } from '@components/layout/Header/Header';
import { ReactNode } from 'react';
import { NavbarTheme } from 'src/types/header-types';
import { useRevalidateOnRefocus } from '@hooks/useSwrApi';
import { HeaderSearchContext } from '@components/contexts/HeaderSearchContext';
import { FiltersQueryParamsStates, JobSearchQueryParams, TalentSearchQueryParams } from '@type/filters';


// Interface
// ******************************************
interface LayoutWithHeaderProps {
  children?: ReactNode,
  navbarTheme?: NavbarTheme,
  searchContext?: FiltersQueryParamsStates<TalentSearchQueryParams | JobSearchQueryParams>,
}


/**
 * @description Layout with header and no footer.
 * - children - The children to render inside the layout.
 * - navbarTheme - The theme of the navbar, 'default' | 'transparent'.
 * - searchContext - The search context to pass to the search input component in the MainNavbar.
 *
 * @example
 * // if you want a transparent header, pass the navbarTheme prop
 * MyPage.getLayout = function getLayout(page: ReactNode) {
 *   return (
 *     <LayoutWithHeader navbarTheme="transparent" searchContext={myObject}>
 *       {page}
 *     </LayoutWithHeader>
 *   );
 * };
 */
export const LayoutWithHeader = function LayoutFunction(props: LayoutWithHeaderProps) {
  // Destructure props
  const {
    children,
    navbarTheme,
    searchContext,
  } = props;

  // Check if we should revalidate SWR cache on tab refocus
  useRevalidateOnRefocus();

  return (
    <HeaderSearchContext.Provider value={searchContext}>
      {/* Place all the sticky top banners in Header component */}
      <Header navbarTheme={navbarTheme} />
      {children}
    </HeaderSearchContext.Provider>
  );
};
